import _get from 'lodash/get';
import _merge from 'lodash/merge';
import _set from 'lodash/set';
import { observable } from 'mobx';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import imagePlaceholder from '@src/slide/domain/ui-model/elements/default-resource/img/image.svg';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import ElementBaseModel from '@src/slide/domain/ui-model/element-modeles/base-modeles/element-base-model';
import {
    IPictureModel,
    IVideoOrAudio,
} from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import {
    IBaseModel,
    IConvertSdkToUiDataOptions,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import {
    IPictureModelProps,
    IPictureModelStyle,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/picture-model-props.interface';
import {
    IGraphicProperty,
    IOperatingGraphicInfo,
} from '@src/slide/domain/ui-model/element-modeles/property/graphic-info/graphic-info.interface';
import { ITextEditor } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/text-editor.interface';
import { GraphicProperty } from '@src/slide/domain/ui-model/element-modeles/property/graphic-info';
import {
    DEFAULT_IMAGE_COLOR,
    DEFAULT_REGULATE,
    ImagePropsField,
} from '@src/slide/domain/ui-model/element-modeles/property/image-operation/constant/props-value';
import { transaction } from '@src/slide/domain/slide-app';
import { IImageSize } from './property/image-size/image-size.interface';
import ImageSize from './property/image-size';
import { FillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/fill-converter';
import { ImageCrop } from './property/image-operation/image-crop';
import { IImageEffect } from './property/image-operation/interface/image-effect.interface';
import ImageEffect from './property/image-operation/image-effect';
import { IImageCrop } from './property/image-operation/interface/image-crop.interface';
import { MediaRegion } from '@src/slide/application/service/media-manager/media-manager.interface';
import { ModelMenuRefreshEventsKeys } from '@src/slide/domain/ui-model/element-modeles/Interface/model-menu-refresh.interface';
import { IPictureDataModel } from '@tencent/slide-sdk-model';
import ModelConverterBridge from '@src/slide/domain/ui-model/element-modeles/convert/model-converter-bridge';
import BaseModel from '@src/slide/domain/ui-model/element-modeles/base-modeles/base-model';

class PictureModel extends ElementBaseModel implements IPictureModel {
    /**
     * 将sdkModel转换为uiModel
     * @param sdkData 合并了继承属性的sdkModel
     * @param uiData 当前uiModel
     * @param rawSdkData 原始sdkModel
     * @param option 附加参数，sdk的observers回调为源头
     */
    public static async convertDataSdkToUi(
        sdkData: IPictureDataModel,
        uiData: IPictureModel,
        rawSdkData: IPictureDataModel,
        option?: IConvertSdkToUiDataOptions,
    ): Promise<void> {
        super.convertDataSdkToUi(sdkData, uiData, rawSdkData, option);

        const convertPromises: Array<Promise<void> | IBaseModel> = [];
        const convertPictureTask = ModelConverterBridge.convertSdkToUi({
            type: UIModelType.IMAGE,
            sdkData,
            uiData,
        });
        convertPromises.push(convertPictureTask);

        if (uiData.parent?.asyncConvertChildren) {
            await Promise.all(convertPromises);
        }
    }
    /**
     * sdk转ui后的操作
     * @param sdkData 合并了继承属性的sdkModel
     * @param uiData 当前uiModel
     * @param rawSdkData 原始sdkModel
     * @param optionObject 附加参数，sdk的observers回调为源头
     */
    public static didConvertDataSdkToUi(
        sdkData: IPictureDataModel,
        uiData: IPictureModel,
        rawSdkData: IPictureDataModel,
        optionObject: IConvertSdkToUiDataOptions,
    ): void {
        super.didConvertDataSdkToUi(sdkData, uiData, rawSdkData, optionObject);
        FillConverter.applyGrpFill(uiData);
        uiData.getMenuManager()?.event.emit(ModelMenuRefreshEventsKeys.REFRESH_MENU_ITEM_VALUES);
        uiData.getMenuManager()?.event.emit(ModelMenuRefreshEventsKeys.REFRESH_GROUP_ITEM_VALUES);
    }
    /**
     * 将ui数据转换为sdk类型
     * @param uiData
     * @param sdkData
     * @param rawUiData
     */
    public static convertDataUiToSdk(uiData: any, sdkData: any, rawUiData: any): void {
        ModelConverterBridge.convertUiToSdk({ type: UIModelType.IMAGE, uiData, sdkData, rawUiData });
    }

    declare public sdkData: IPictureDataModel | null;
    public props: IPictureModelProps;
    public editorInstance: ITextEditor | null;
    /**
     * 关键点操作临时数据
     */
    @observable public operatingGraphicInfo: IOperatingGraphicInfo | null = null;
    @observable public isPlaying = true;
    public video?: IVideoOrAudio;
    public audio?: IVideoOrAudio;
    private graphicProperty: IGraphicProperty;
    private imageSizeInstance: IImageSize;  // 图片原始尺寸相关
    private imageCropInstance: ImageCrop;
    private imageEffectInstance: IImageEffect;

    public constructor() {
        super();
        this.type = UIModelType.IMAGE;
        this.props = {
            src: imagePlaceholder,
            style: {
                left: 0,
                top: 0,
                isImage: true, // 给边框用,图片默认连接处是圆角
                width: ptToCanvas(281),
                height: ptToCanvas(200),
                opacity: 1,
                position: 'absolute',
            },
        };
    }

    public isAttachment(): boolean {
        return !!this.props?.attachment;
    }

    public isAttachmentUploaded(): boolean {
        const attachment = _get(this, 'props.attachment');
        return !!attachment?.objKey;
    }

    /**
     * 是否为音视频
     */
    public isMedia(): boolean {
        return this.isVideo() || this.isAudio();
    }

    public isVideo(): boolean {
        return !!this.video;
    }

    public isAudio(): boolean {
        return !!this.audio;
    }

    /**
     * 获取音视频体积
     */
    public getMediaSize(): number | undefined {
        if (this.video) return this.video.size;
        if (this.audio) return this.audio.size;
    }

    /**
     * 图形信息接口
     */
    public getGraphic(): IGraphicProperty {
        if (!this.graphicProperty) {
            this.graphicProperty = new GraphicProperty(this);
        }
        return this.graphicProperty;
    }

    public handleAlpha(values, options): void {
        transaction(() => {
            // 当图片更新图片填充透明度时，若为图片填充，则图片填充透明度也会更新
            const fillInfo = this.getBackgroundFill().getFillInfo();
            if (fillInfo?.image) {
                this.getBackgroundFill().updateCurrentFill(
                    // @ts-ignore
                    { image: { ...fillInfo.image, alpha: String(values.image.alpha) } },
                    options?.onlyChangeUI,
                );
            }
            this.updateImageAlpha(values, options);
        });
    }

    public handleRotate(): void {
        let rotate = 15; // shirt + 旋转为15度为单元进行
        if (this.props.style.rotate) {
            rotate = this.props.style.rotate + 15;
            rotate = rotate % 360;
        }
        _set(this, 'props.style.rotate', rotate);
        const v3Data = { style: { rotate } };
        this.updateProps(v3Data);
    }

    public handleResetPicture(needResetSize?: boolean, needResetAlpha = true): void {
        const { style } = this.props;
        let newStyle: any = {
            image: {
                ...DEFAULT_REGULATE,
                ...DEFAULT_IMAGE_COLOR,
                grayscl: null,
                duotone: null,
                biLevel: null,
                luminance: null,
            },
        };

        // 1. 重置裁剪形状
        if (style.bgShape !== 'rect') {
            newStyle = _merge(newStyle, {
                bgShape: 'rect',
                controlValues: [],
            });
        }

        // 2. 重置透明度
        if (needResetAlpha && style.alpha !== 1) {
            newStyle = _merge(newStyle, {
                alpha: 1,
            });
        }

        // 4. 更新数据
        transaction(() => {
            // 如需重置大小，则重置大小
            needResetSize && this.getImageSize().resetSize();
            this.resetAllProps(newStyle);
            this.reset3DProps();
        });
    }

    public getImageCrop(): IImageCrop {
        if (!this.imageCropInstance) {
            this.imageCropInstance = new ImageCrop(this);
        }
        return this.imageCropInstance;
    }

    public canSetArrowStyle(): boolean {
        if (!this.pathParser) {
            return false;
        }
        const pathList = this.pathParser.svgPathInRect();
        // 多段path时，无需考虑首尾重合的情况，只需判断是否有闭合的指令即可
        if (pathList.length > 1) {
            return pathList.some(path => path.svgPath.indexOf('Z') === -1 && path.svgPath.indexOf('z') === -1);
        }
        return this.getGraphic().isPathOpened(pathList[0].svgPath);
    }

    /**
     * 图片替换
     * @param values
     */
    // eslint-disable-next-line complexity
    public handleReplacePicture(values): void {
        // 裁剪图片
        const getSrcRect = (imageObj) => {
            if (imageObj.srcRect) {
                const { l = 0, r = 0, t = 0, b = 0 } = imageObj.srcRect;
                return { l, r, t, b };
            }
            // 默认短边平铺长边裁剪
            const imgWidth = imageObj.width;
            const imgHeight = imageObj.height;
            const frameWidth = this.props.style.width;
            const frameHeight = this.props.style.height;
            const ratioX = frameWidth / imgWidth;
            const ratioY = frameHeight / imgHeight;
            const ratio = ratioX < ratioY ? ratioY : ratioX;
            const srcRectRatioW = ((imgWidth * ratio) - frameWidth) / (imgWidth * ratio * 2);
            const srcRectRatioH = ((imgHeight * ratio) - frameHeight) / (imgHeight * ratio * 2);
            return {
                l: srcRectRatioW,
                r: srcRectRatioW,
                t: srcRectRatioH,
                b: srcRectRatioH,
            };
        };
        // 视频替换封面时取消播放
        if (this.isVideo()) {
            const mediaManager = this.getMediaManager();
            mediaManager?.pauseVideoByRegion(this.key, MediaRegion.EDITOR);
        }
        if (Array.isArray(values) && values.length > 0) {
            const imageObj = values[0] || {};

            // 视频元素不支持替换 gif 图
            if (this.isVideo() && imageObj?.imageType === 'gif') {
                return;
            }

            const frameWidth = this.props.style.width;
            const frameHeight = this.props.style.height;
            if (frameHeight > 0 && frameWidth > 0) {
                this.updateForegroundImage({
                    src: imageObj.src,
                    backupSrc: imageObj.backupSrc || '',
                    style: {
                        alpha: 1,
                        // video 不裁剪
                        srcRect: !this.isVideo() && getSrcRect(imageObj),
                        image: {
                            brightness: ImagePropsField.BRIGHTNESS.defaultValue,
                            colorTemperature: ImagePropsField.COLORTEMPERATURE.defaultValue,
                            contrast: ImagePropsField.CONTRAST.defaultValue,
                            saturate: ImagePropsField.SATURATE.defaultValue,
                            sharpenSoften: ImagePropsField.ClARITY.defaultValue,
                        },
                    },
                    imageType: imageObj.imageType,
                });
            }
        }
    }

    /**
     * 是否为图片。如果audio/video存在则是本地音频/视频。
     */
    public isImage = (): boolean => !(this.audio || this.video || this.isAttachment());

    /**
     * 图片原始尺寸相关
     */
    public getImageSize(): IImageSize {
        if (!this.imageSizeInstance) {
            this.imageSizeInstance = new ImageSize(this);
        }
        return this.imageSizeInstance;
    }


    /**
     * 编辑公式 - 更换公式图片
     * 产品逻辑：
     * 编辑公式时，新旧公式图片，以图片中心点为基准，删除旧公式，重新插入新公式，不继承缩放、剪裁效果，集成旋转、边框、发光阴影等效果
     *
     * 技术逻辑：
     * 1. 以原图片外框为基准，获取并记录外框中心点坐标
     * 2. 将新图片像素宽高转换为dom宽高，用于与前端元素之间的数学计算
     * 3. 将新图片的dom宽高作为外框的新宽高，以中心点坐标为基准，计算外框的position位置，具体表现为left和top的数值
     * 4. 将srcRect的属性重置为0，使新图片填充满外框
     * 5. 替换原图片的src链接为新图片
     * 6. 更新此picture-model的props
     */
    public updateLatex(obj): void {
        const image = obj;

        // 转换图片宽高计算单位：像素距离 => dom距离
        const imageCanvas = {
            width: ptToCanvas(image.width),
            height: ptToCanvas(image.height),
        };

        // 获取frame外框的宽、高和position的left、top位置
        const {
            width: frameWidth,
            height: frameHeight,
            left: frameLeft,
            top: frameTop,
        } = this.props.style;

        // frame外框的中心点坐标
        const frameCenterCoordinates = {
            x: frameLeft + (frameWidth / 2),
            y: frameTop + (frameHeight / 2),
        };

        // 更新公式图片model的props
        this.updateProps({
            src: image.src,
            addon: image.addon,
            style: {
                alpha: 1,
                srcRect: { l: 0, r: 0, t: 0, b: 0 },
                width: imageCanvas.width,
                height: imageCanvas.height,
                left: frameCenterCoordinates.x - (imageCanvas.width / 2),
                top: frameCenterCoordinates.y - (imageCanvas.height / 2),
            },
        });
    }

    public getImageEffect(): IImageEffect {
        if (!this.imageEffectInstance) {
            this.imageEffectInstance = new ImageEffect(this);
        }
        return this.imageEffectInstance;
    }

    public updateStyle(style: Partial<IPictureModelStyle>) {
        this.updateProps({ style });
    }

    public updateMediaProps(mediaProps: IVideoOrAudio) {
        const key = this.video ? 'video' : 'audio';
        const sdkData = this.prepareSDKData({
            [key]: { ...this[key], ...mediaProps },
            parent: this.parent,
        });
        BaseModel.sdkV3UpdateProps(this.sdkData, sdkData);
    }

    private updateForegroundImage(props) {
        if (!this.sdkData) return;
        const sProps = this.prepareSDKPropsData(props);
        BaseModel.sdkV3UpdateProps(this.sdkData, sProps);
    }

    /**
     * 重置所有属性，计算差异，得出需要重置的属性：填充、边框、Effect（阴影、发光、柔滑边缘、映像）
     */
    private resetAllProps = (childStyle) => {
        const { style } = this.props;
        const removeStyle: Partial<IPictureModelStyle> = {};
        let resetStyle = { ...childStyle };
        if (style.border) {
            resetStyle = _merge(resetStyle, {
                border: {
                    color: null,
                    width: 0,
                },
            });
            this.getBorder?.().handleBorderSpecial(resetStyle.border);
        }
        if (_get(style, 'fillBackground.noFill') !== 1) {
            resetStyle = _merge(resetStyle, {
                fillBackground: {
                    noFill: 1,
                },
            });
        }
        if (style.effect) {
            Object.keys(style.effect).forEach((cur) => {
                removeStyle.effect = removeStyle.effect || {};
                removeStyle.effect[cur] = null;
            });
        }
        this.updateAndRemoveProps({ style: resetStyle }, { style: removeStyle });
    };

    private reset3DProps = () => {
        this.removeProps({ scene3d: null, shape3d: null });
    };

    private updateImageAlpha(values, options) {
        if (options?.onlyChangeUI) {
            _set(this, 'props.style.alpha', values.image.alpha);
            this.previewChange += 1; // 触发ui刷新
        } else {
            const v3Data = { style: { alpha: values.image.alpha } };
            this.updateProps(v3Data);
        }
    }
}

export default PictureModel;
